import { useState, type FormEvent } from "react";

import { useI18n } from "../../lib/i18n";

const ALLOWED = ["image/jpeg", "image/png", "image/webp"];
const MAX_BYTES = 6 * 1024 * 1024;

// `manageToken` is passed only by the owner dashboard. It lets the server
// record the upload as the club's own photo (source 'owner') rather than an
// anonymous visitor's.
export function PhotoUpload({ clubSlug, manageToken }: { clubSlug: string; manageToken?: string }) {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [errorKey, setErrorKey] = useState("photo_err_generic");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      setErrorKey("photo_err_type");
      setState("error");
      return;
    }
    if (file.size > MAX_BYTES) {
      setErrorKey("photo_err_size");
      setState("error");
      return;
    }
    setState("sending");
    try {
      const form = new FormData();
      form.set("clubSlug", clubSlug);
      form.set("name", name);
      form.set("file", file);
      if (manageToken) form.set("token", manageToken);
      const res = await fetch("/api/upload-photo", { method: "POST", body: form });
      const data = (await res.json()) as { ok: boolean };
      setState(data.ok ? "done" : "error");
      if (!data.ok) setErrorKey("photo_err_generic");
    } catch {
      setErrorKey("photo_err_generic");
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <div className="rounded-xl border border-[#2C4FD6]/40 bg-[#2C4FD6]/5 p-5">
        <p className="li-display font-bold text-[#1C2430]">{t("photo_ok_t")}</p>
        <p className="mt-1 text-sm leading-relaxed text-[#5B6472]">{t("photo_ok_d")}</p>
      </div>
    );
  }

  const label = "li-mono mb-1 block text-[11px] uppercase tracking-wider text-[#5B6472]";

  return (
    <form onSubmit={submit} className="rounded-xl border border-[#1C2430]/10 bg-white/50 p-5">
      <h3 className="li-display font-bold tracking-tight text-[#1C2430]">{t("add_photo_title")}</h3>
      <p className="mt-1 text-sm text-[#5B6472]">{t("add_photo_sub")}</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="ph-name" className={label}>
            {t("photo_name")}
          </label>
          <input
            id="ph-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="li-display h-11 w-full rounded-lg border border-[#1C2430]/15 bg-white/70 px-3 text-sm text-[#1C2430] focus:border-[#2C4FD6] focus:outline-none"
          />
        </div>
        <div>
          <label htmlFor="ph-file" className={label}>
            {t("photo_file")}
          </label>
          <input
            id="ph-file"
            type="file"
            required
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="li-display block w-full text-sm text-[#1C2430] file:mr-3 file:rounded-md file:border-0 file:bg-[#1C2430] file:px-3 file:py-2 file:text-[12px] file:text-[#F2EFE9]"
          />
        </div>
      </div>
      {state === "error" ? <p className="mt-3 text-sm text-[#B03030]">{t(errorKey)}</p> : null}
      <button
        type="submit"
        disabled={state === "sending" || !file}
        className="li-mono mt-4 h-10 rounded-lg border border-[#1C2430]/25 px-4 text-[12px] text-[#1C2430] transition-colors hover:border-[#2C4FD6] disabled:opacity-50"
      >
        {state === "sending" ? t("photo_sending") : t("photo_send")}
      </button>
    </form>
  );
}
